import {
  useGetMeetingsQuery,
  useLazyGetMeetingsQuery,
  useLazyGetMeetingQuery,
} from 'features/meetings/graphql_api'

/**
 * Stable empty object for visible-range query when disabled — avoids new reference each render
 * and duplicate RTK Query subscription keys.
 */
const EMPTY_QUERY_VARS = {}

/**
 * RTK Query subscriptions for meetings list/detail via the legacy GraphQL API.
 * Used by `useSearchDataLegacy`. Pass `skipQueries || isSupergraphEnabled` on each query's `skip`
 * when both backends mount (e.g. `useMeetingsSearchData` skips this path when SuperGraph is on).
 */
export const useLegacyMeetingSearchQueries = ({
  skipQueries,
  isSupergraphEnabled,
  shouldLoadToday,
  filters,
  visibleRangeQueryVariables,
  queryOptions,
  backgroundPollingConfig,
  visibleRangePollingInterval,
}) => {
  const futureSearchApiVars = filters ? { ...filters, order: 'ASC' } : null

  const futureSearchApi = useGetMeetingsQuery(
    futureSearchApiVars || {},
    {
      skip: skipQueries || isSupergraphEnabled,
      ...queryOptions,
    },
  )

  const todaySearchApiVars = filters ? { ...filters, today: true } : null

  const todaySearchApi = useGetMeetingsQuery(
    todaySearchApiVars || {},
    {
      skip: skipQueries || !shouldLoadToday || isSupergraphEnabled,
      ...queryOptions,
    },
  )

  const pastSearchApiVars = filters ? { ...filters, past: true, order: 'DESC' } : null

  const pastSearchApi = useGetMeetingsQuery(
    pastSearchApiVars || {},
    {
      skip: skipQueries || isSupergraphEnabled,
      ...queryOptions,
    },
  )

  const visibleRangeArgs = visibleRangeQueryVariables ?? EMPTY_QUERY_VARS
  const { data: visibleRangeDataSearch } = useGetMeetingsQuery(visibleRangeArgs, {
    ...backgroundPollingConfig,
    pollingInterval: visibleRangePollingInterval,
    skip: !visibleRangeQueryVariables || isSupergraphEnabled,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  const [triggerSearchApi, resultSearchApi] = useLazyGetMeetingsQuery()
  const [triggerVisibleRangeNextSearchApi, visibleRangeNextResultSearchApi] = useLazyGetMeetingsQuery()
  const [triggerMeetingSearchApi] = useLazyGetMeetingQuery()

  return {
    futureSearchApi,
    todaySearchApi,
    pastSearchApi,
    visibleRangeDataSearch,
    triggerSearchApi,
    resultSearchApi,
    triggerVisibleRangeNextSearchApi,
    visibleRangeNextResultSearchApi,
    triggerMeetingSearchApi,
  }
}
